import { z } from "zod";
import { Id } from "./ids.js";

/** Which memory the view reads: the bot's own, or what it shares: its zone with a provider, the Space notes without one. */
export const SemanticMemoryViewInputSchema = z.object({
  botId: Id,
  shared: z.boolean().default(false),
  query: z.string().trim().max(500).optional(),
});
export type SemanticMemoryViewInput = z.infer<typeof SemanticMemoryViewInputSchema>;

export const SemanticMemoryItemSchema = z.object({
  /** Provider fact id, or the memory document holding the line. */
  key: z.string(),
  text: z.string(),
  updatedAt: z.string().optional(),
  /** Provider namespace the fact came from; passed back to forget it. */
  entity: z.string().optional(),
  canForget: z.boolean(),
});
export type SemanticMemoryItem = z.infer<typeof SemanticMemoryItemSchema>;

export const SemanticMemoryViewSchema = z.object({
  /** "provider" when the Space connected a memory provider, else the bot's memory notes. */
  source: z.enum(["provider", "documents"]),
  /** The provider's id, e.g. "supermemory"; null for memory notes. */
  provider: z.string().nullable(),
  /** The provider lists facts only in answer to a search. */
  searchOnly: z.boolean(),
  items: z.array(SemanticMemoryItemSchema),
});
export type SemanticMemoryView = z.infer<typeof SemanticMemoryViewSchema>;

export const SemanticMemoryForgetInputSchema = z.object({
  botId: Id,
  shared: z.boolean().default(false),
  key: z.string().min(1).max(500),
  /** The line to remove from a memory note; ignored for provider facts. */
  text: z.string().max(20_000).optional(),
  entity: z.string().max(500).optional(),
});
export type SemanticMemoryForgetInput = z.infer<typeof SemanticMemoryForgetInputSchema>;

/**
 * A bot's memory at a glance, read-only: the lines of its pinned MEMORY.md (its own and its
 * zone's), and its latest remembered items, newest first.
 */
export const BotMemoryOverviewSchema = z.object({
  pinned: z.array(z.object({ key: z.string(), text: z.string(), zone: z.string().nullable() })),
  latest: z.array(
    z.object({
      key: z.string(),
      text: z.string(),
      updatedAt: z.string().optional(),
      /** The zone a note came from; absent for the bot's own memory. */
      zone: z.string().nullable().optional(),
    }),
  ),
});
export type BotMemoryOverview = z.infer<typeof BotMemoryOverviewSchema>;
